import {
   ChangeDetectorRef,
   Component,
   ElementRef,
   OnDestroy,
   OnInit,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { AppConfigService } from 'src/app/app-config/app-config-service';
import { ConfirmationDialogComponent } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddDashboardTileComponent } from '../add-dashboard-tile/containers/add-dashboard-tile.component';
import { FullscreenDashboardTile } from '../models/dashboard-tile.model';
import { Dashboard } from '../models/dashboard.model';
import * as DashboardActions from '../state/dashboard.actions';
import {
   getCallState,
   getDashboards,
   getFullscreenTile,
   getPreservedLayoutFromDashboard,
   getSelectedDashboard,
} from '../state/dashboard.selectors';
import { DashboardGridLayoutItem, DashboardGridLayoutItemConfigurationInput } from '../views/dashboard-view.component';
import { AddDashboardDialogComponent } from './add-dashboard-dialog/add-dashboard-dialog.component';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-dashboard',
    host: {
        '[class.fullscreenOpen]': 'hasFullscreenTile',
    },
    styleUrls: ['./dashboard.component.scss'],
    templateUrl: './dashboard.component.html',
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
   @ViewChild('fullscreenRef') fullscreenRef: ElementRef;

   dashboards$: Observable<Dashboard[] | null>;

   fullscreenTile$: Observable<FullscreenDashboardTile | null>;

   callState$: Observable<CallState>;

   adminToken: string | null;

   selectedDashboard$: Observable<Dashboard | null>;

   preservedLayout$: Observable<DashboardGridLayoutItem[] | null>;

   hasScrolled: boolean = false;

   isEditing: boolean = false;

   addDashboardCancel: boolean = false;

   hasFullscreenTile: boolean = false;

   private destroyed$ = new Subject<void>();

   private addDashboard$ = new Subject<void>();

   private addDashboardDialogRef: MatDialogRef<AddDashboardDialogComponent>;

   private addTileDialogRef: MatDialogRef<AddDashboardTileComponent>;

   private deleteDashboardDialogRef: MatDialogRef<ConfirmationDialogComponent, boolean>;

   constructor(
      private appConfigService: AppConfigService,
      private store: Store,
      private ref: ElementRef,
      private dialogService: LcsDialogService,
      private confirmationDialogService: ConfirmationDialogService,
      private changeDetectorRef: ChangeDetectorRef
   ) {
      this.adminToken = this.appConfigService.getConfig().adminToken;
      this.dashboards$ = this.store.select(getDashboards);
      this.selectedDashboard$ = this.store.select(getSelectedDashboard);
      this.preservedLayout$ = this.store.select(getPreservedLayoutFromDashboard);
      this.fullscreenTile$ = this.store.select(getFullscreenTile);
      this.fullscreenTile$.pipe(takeUntil(this.destroyed$)).subscribe((fullscreenTile) => {
         this.hasFullscreenTile = !!fullscreenTile;
         requestAnimationFrame(() => {
            if (this.ref && this.fullscreenRef?.nativeElement && fullscreenTile) {
               this.fullscreenRef.nativeElement.style.top = this.ref.nativeElement.scrollTop + 'px';
            }
         });
      });
   }

   ngOnInit(): void {
      this.store.dispatch(DashboardActions.pageEntered());
      this.callState$ = this.store.select(getCallState);

      this.addDashboard$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.addDashboardDialogRef.close();
      });

      this.addDashboard$
         .pipe(withLatestFrom(this.selectedDashboard$), takeUntil(this.destroyed$))
         .subscribe(([_, selectedDashboard]) => {
            this.isEditing = true;
         });
      this.preservedLayout$ = this.store.select(getPreservedLayoutFromDashboard);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.addDashboardDialogRef?.close();
      this.deleteDashboardDialogRef?.close();
      this.addTileDialogRef?.close();
   }

   onLayoutUpdated(payload: {
      dashboardTileLayoutConfigurationInput: DashboardGridLayoutItemConfigurationInput[];
      columnLayoutSize: number;
   }) {
      this.store.dispatch(DashboardActions.saveGridLayoutChange({ ...payload }));
      this.changeDetectorRef.detectChanges();
   }

   onAddDashboard() {
      this.addDashboardDialogRef = this.dialogService.open<AddDashboardDialogComponent>(AddDashboardDialogComponent, {
         data: { dashboards: this.dashboards$ },
         disableClose: true,
         panelClass: [DialogSize.Medium, 'dialog-container'],
      });
      this.addDashboardCancel = false;
      const component = this.addDashboardDialogRef.componentInstance;

      // Outputs()
      const submitSubscription: Subscription = component.submit.subscribe((form) => {
         if (form.valid) {
            this.store.dispatch(
               DashboardActions.addDashboard({
                  name: form.value.name,
               })
            );
            this.addDashboard$.next();
         }
      });

      const cancelSubscription: Subscription = component.close.subscribe((form) => {
         this.addDashboardCancel = true;
         this.changeDetectorRef.detectChanges();
      });

      this.addDashboardDialogRef.afterClosed().subscribe(() => {
         submitSubscription.unsubscribe();
         cancelSubscription.unsubscribe();
      });
   }

   onRenameDashboard(newDashboardName: string) {
      this.store.dispatch(
         DashboardActions.renameDashboard({
            name: newDashboardName,
         })
      );
   }

   onEditToggle(isEditing) {
      this.isEditing = isEditing;
      this.changeDetectorRef.detectChanges();
   }

   onAddTile() {
      // Select tiles
      this.addTileDialogRef = this.dialogService.open(AddDashboardTileComponent, {
         disableClose: true,
         panelClass: ['dialog-xl', 'dialog-container'],
      });
   }

   onDeleteDashboard() {
      this.deleteDashboardDialogRef = this.confirmationDialogService.confirm({
         data: {
            header: 'Delete Dashboard?',
            message: 'Are you sure you want to delete this dashboard and all the tiles on it?',
         },
         size: DialogSize.Medium,
      });
      this.deleteDashboardDialogRef.afterClosed().subscribe((confirmed) => {
         if (confirmed) {
            this.store.dispatch(DashboardActions.deleteDashboard());
         }
      });
   }

   onScroll(event) {
      this.hasScrolled = event.target.scrollTop > 0;
   }
}
